import type { AxiosRequestConfig } from 'axios'
import type {
  IReview,
  IReportReviewMacro,
  IReviewTopic,
  IReviewTopicPayload,
  IReviewReplyMacroPayload,
  IReviewReportMacroPayload,
  IReplyReviewMacro,
  IReportReviewsPayload,
  IReportReviewsResponse,
  IReplyToReviewsPayload,
  IReplyToReviewsResponse,
} from '~/types/review'

import config from '~/config'
import { paramsSerializer, toEmptyArrayIfNull } from '~/utils/api'
import { request } from '~/utils/request'

const reviewsFiltersKeyName = (key: string) => {
  switch (key) {
    case 'appVersions':
      return 'app_version'
    case 'countries':
      return 'country_code'
    case 'mlLabels':
      return 'ml_labels'
    case 'ratings':
      return 'rating'
    case 'statuses':
      return 'status'
    case 'statusFilterType':
      return 'status_filter_type'
    default:
      return key
  }
}

/** Reviews */

/** @doc https://api-dev-europe-west1-stiqrwpqqa-ew.a.run.app/#get-/admin/reviews */
export const getReviews = (params?: AxiosRequestConfig['params']): Promise<IPaginatedResponse<IReview>> => {
  return request({
    url: `${config.ADMIN_REVIEWS_API}/admin/reviews?${paramsSerializer(params, reviewsFiltersKeyName)}`,
  })
}

/** @doc https://api-dev-europe-west1-stiqrwpqqa-ew.a.run.app/#get-/admin/reviews/languages */
export const getReviewLanguagesList = (): Promise<string[]> => {
  return request({
    url: `${config.ADMIN_REVIEWS_API}/admin/reviews/languages`,
  })
}

/** @doc https://api-dev-europe-west1-stiqrwpqqa-ew.a.run.app/#get-/admin/reviews/app_versions */
export const getReviewAppVersions = (params?: AxiosRequestConfig['params']): Promise<string[]> => {
  return request({
    params,
    url: `${config.ADMIN_REVIEWS_API}/admin/reviews/app_versions`,
  })
}

/** @doc https://api-dev-europe-west1-stiqrwpqqa-ew.a.run.app/#get-/admin/reviews/-reviewID-/history */
export const getReviewHistory = (reviewId: string): Promise<IReview[]> => {
  return request({
    transformResponse: toEmptyArrayIfNull,
    url: `${config.ADMIN_REVIEWS_API}/admin/reviews/${reviewId}/history`,
  })
}

/** @doc https://api-dev-europe-west1-stiqrwpqqa-ew.a.run.app/#put-/admin/reviews/-reviewID-/topics */
export const updateReviewTopics = (review: IReview): Promise<void> => {
  return request({
    data: { topics: review.topics },
    method: 'PUT',
    url: `${config.ADMIN_REVIEWS_API}/admin/reviews/${review.id}/topics`,
  })
}

/** @doc https://api-dev-europe-west1-stiqrwpqqa-ew.a.run.app/#post-/admin/reviews/reply */
export const replyToReviews = (payload: IReplyToReviewsPayload): Promise<IReplyToReviewsResponse> => {
  return request({
    data: payload,
    method: 'POST',
    url: `${config.ADMIN_REVIEWS_API}/admin/reviews/reply`,
  })
}

/** @doc https://api-dev-europe-west1-stiqrwpqqa-ew.a.run.app/#post-/admin/reviews/report */
export const reportReviews = (payload: IReportReviewsPayload): Promise<IReportReviewsResponse> => {
  return request({
    data: payload,
    method: 'POST',
    url: `${config.ADMIN_REVIEWS_API}/admin/reviews/report`,
  })
}

/** @doc https://api-dev-europe-west1-stiqrwpqqa-ew.a.run.app/#post-/admin/reviews/-reviewID-/do_not_report */
export const doNotReportReview = ({ reviewId }: { reviewId: string }): Promise<void> => {
  return request({
    method: 'POST',
    url: `${config.ADMIN_REVIEWS_API}/admin/reviews/${reviewId}/do_not_report`,
  })
}

/** Topics */

/** @doc https://api-dev-europe-west1-stiqrwpqqa-ew.a.run.app/#post-/admin/topic */
export const createReviewTopic = (payload: IReviewTopicPayload): Promise<IReviewTopic> => {
  return request({
    data: payload,
    method: 'POST',
    url: `${config.ADMIN_REVIEWS_API}/admin/topic`,
  })
}

/** @doc https://api-dev-europe-west1-stiqrwpqqa-ew.a.run.app/#get-/admin/topic */
export const getReviewsTopics = (): Promise<IReviewTopic[]> => {
  return request({
    transformResponse: toEmptyArrayIfNull,
    url: `${config.ADMIN_REVIEWS_API}/admin/topic`,
  })
}

/** @doc https://api-dev-europe-west1-stiqrwpqqa-ew.a.run.app/#get-/admin/topic_category */
export const getReviewsTopicsCategories = (): Promise<string[]> => {
  return request({
    transformResponse: toEmptyArrayIfNull,
    url: `${config.ADMIN_REVIEWS_API}/admin/topic_category`,
  })
}

/** @doc https://api-dev-europe-west1-stiqrwpqqa-ew.a.run.app/#delete-/admin/topic/-topicID- */
export const deleteReviewTopic = ({ topicId }: { topicId: string }): Promise<void> => {
  return request({
    method: 'DELETE',
    url: `${config.ADMIN_REVIEWS_API}/admin/topic/${topicId}`,
  })
}

/** @doc https://api-dev-europe-west1-stiqrwpqqa-ew.a.run.app/#put-/admin/topic/-topicID- */
export const updateReviewTopic = ({
  payload,
  topicId,
}: {
  payload: IReviewTopicPayload
  topicId: string
}): Promise<IReviewTopic> => {
  return request({
    data: payload,
    method: 'PUT',
    url: `${config.ADMIN_REVIEWS_API}/admin/topic/${topicId}`,
  })
}

/** Report Macros */

/** @doc https://api-dev-europe-west1-stiqrwpqqa-ew.a.run.app/#get-/admin/report_macro */
export const getReviewsReportMacros = (params?: AxiosRequestConfig['params']): Promise<IReportReviewMacro[]> => {
  return request({
    params,
    transformResponse: toEmptyArrayIfNull,
    url: `${config.ADMIN_REVIEWS_API}/admin/report_macro`,
  })
}

/** @doc https://api-dev-europe-west1-stiqrwpqqa-ew.a.run.app/#post-/admin/report_macro */
export const createReviewReportMacro = (payload: IReviewReportMacroPayload): Promise<IReportReviewMacro> => {
  return request({
    data: payload,
    method: 'POST',
    url: `${config.ADMIN_REVIEWS_API}/admin/report_macro`,
  })
}

/** @doc https://api-dev-europe-west1-stiqrwpqqa-ew.a.run.app/#delete-/admin/report_macro/-reportMacroID- */
export const deleteReviewReportMacro = ({ macroId }: { macroId: string }): Promise<void> => {
  return request({
    method: 'DELETE',
    url: `${config.ADMIN_REVIEWS_API}/admin/report_macro/${macroId}`,
  })
}

/** @doc https://api-dev-europe-west1-stiqrwpqqa-ew.a.run.app/#put-/admin/report_macro/-reportMacroID- */
export const updateReviewReportMacro = ({
  macroId,
  payload,
}: {
  macroId: string
  payload: IReviewReportMacroPayload
}): Promise<IReportReviewMacro> => {
  return request({
    data: payload,
    method: 'PUT',
    url: `${config.ADMIN_REVIEWS_API}/admin/report_macro/${macroId}`,
  })
}

/** Reply Macros */

/** @doc https://api-dev-europe-west1-stiqrwpqqa-ew.a.run.app/#post-/admin/reply_macro */
export const createReviewReplyMacro = (payload: IReviewReplyMacroPayload): Promise<IReplyReviewMacro> => {
  return request({
    data: payload,
    method: 'POST',
    url: `${config.ADMIN_REVIEWS_API}/admin/reply_macro`,
  })
}

/** @doc https://api-dev-europe-west1-stiqrwpqqa-ew.a.run.app/#get-/admin/reply_macro */
export const getReviewsReplyMacro = (params?: AxiosRequestConfig['params']): Promise<IReplyReviewMacro[]> => {
  return request({
    params,
    transformResponse: toEmptyArrayIfNull,
    url: `${config.ADMIN_REVIEWS_API}/admin/reply_macro`,
  })
}

/** @doc https://api-dev-europe-west1-stiqrwpqqa-ew.a.run.app/#get-/admin/reply_macro_name */
export const getReviewsReplyMacroNames = (): Promise<string[]> => {
  return request({
    transformResponse: toEmptyArrayIfNull,
    url: `${config.ADMIN_REVIEWS_API}/admin/reply_macro_name`,
  })
}

/** @doc https://api-dev-europe-west1-stiqrwpqqa-ew.a.run.app/#delete-/admin/reply_macro/-replyMacroID- */
export const deleteReviewReplyMacro = ({ macroId }: { macroId: string }): Promise<void> => {
  return request({
    method: 'DELETE',
    url: `${config.ADMIN_REVIEWS_API}/admin/reply_macro/${macroId}`,
  })
}

/** @doc https://api-dev-europe-west1-stiqrwpqqa-ew.a.run.app/#put-/admin/reply_macro/-replyMacroID- */
export const updateReviewReplyMacro = ({
  macroId,
  payload,
}: {
  macroId: string
  payload: IReviewReplyMacroPayload
}): Promise<IReplyReviewMacro> => {
  return request({
    data: payload,
    method: 'PUT',
    url: `${config.ADMIN_REVIEWS_API}/admin/reply_macro/${macroId}`,
  })
}
